/* ──────────────────────────────────────────────────────────────
   src/pages/reports/clientes/ProgresoAlumnoPage.tsx
   Informe de progreso (horas hechas vs. totales) por asignatura
   ────────────────────────────────────────────────────────────── */
import { useState } from "react";
import dayjs from "dayjs";
import {
  Stack,
  Paper,
  TextField,
  Typography,
  LinearProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  InputAdornment,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { useClientes } from "../../../hooks/useClientes";
import { useProgresoAlumnoReport } from "../../../hooks/useReportJob";
import { downloadReportFile } from "../../../components/utils/downloadXlsx";

/* ──────────────────────────────────────────────
   Shape que devuelve el worker (para tipar mejor)
   ────────────────────────────────────────────── */
export interface ProgresoAlumnoRow {
  asignatura: string;
  proyecto: string;
  horasTotal: number; // contratadas  (≥ 0)
  horasHechas: number; // consumidas   (≥ 0)
  ultimaSesion: string; // YYYY-MM-DD  |  '—'
}

export default function ProgresoAlumnoPage() {
  const { t } = useTranslation<"translation">();
  const { rows: alumnos } = useClientes();

  /* filtros */
  const [desde, setDesde] = useState(dayjs().format("YYYY-MM-DD"));
  const [selId, setSelId] = useState<number>(); // alumno seleccionado
  const [busc, setBusc] = useState(""); // texto búsqueda

  /* worker (enqueue + polling) */
  const { run, useStatus } = useProgresoAlumnoReport();
  const [jobId, setJobId] = useState<string>();
  const { data: status } = useStatus(jobId);

  const lanzar = async () => {
    if (!selId) return;
    const { jobId } = await run({ alumnoId: selId, desde });
    setJobId(jobId);
  };

  const handleDownload = async () => {
    if (status?.result?.url) {
      try {
        await downloadReportFile(status.result.url);
      } catch (error) {
        console.error("Error al descargar:", error);
      }
    }
  };

  /* lista filtrada de alumnos */
  const lista = alumnos.filter((a) => a.nombreCompleto.toLowerCase().includes(busc.toLowerCase()));

  /* ───────────────────────── UI ───────────────────────── */
  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t("progresoAlumno.titulo")}</Typography>

      {/* selector alumno + fecha */}
      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          size="small"
          placeholder={t<string>("progresoAlumno.filtros.buscar")}
          value={busc}
          onChange={(e) => setBusc(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 260 }}
        />

        <TextField
          select
          SelectProps={{ native: true }}
          label={t("progresoAlumno.filtros.alumno")}
          value={selId ?? ""}
          onChange={(e) => setSelId(Number(e.target.value))}
          sx={{ minWidth: 240 }}
        >
          <option value="" disabled>
            {t("progresoAlumno.filtros.placeholder")}
          </option>
          {lista.map((a) => (
            <option key={a.id} value={a.id}>
              {a.nombreCompleto}
            </option>
          ))}
        </TextField>

        <TextField
          label={t("progresoAlumno.filtros.desde")}
          type="date"
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
        />

        <Button variant="contained" onClick={lanzar} disabled={!selId}>
          {t("progresoAlumno.filtros.generar")}
        </Button>
      </Stack>

      {/* progreso del worker */}
      {status && !status.done && (
        <Stack spacing={1}>
          <LinearProgress variant="determinate" value={status.progress} />
          <Typography variant="body2">
            {t("progresoAlumno.estado.generando", {
              progress: status.progress,
            })}
          </Typography>
        </Stack>
      )}

      {/* resultado */}
      {status?.done && status.result && (
        <>
          <Alert severity="success">
            {t("progresoAlumno.estado.exito")}{" "}
            <Button
              variant="text"
              size="small"
              onClick={handleDownload}
              sx={{ textTransform: "none", textDecoration: "underline", p: 0, minWidth: "auto" }}
            >
              {t("progresoAlumno.estado.descargar")}
            </Button>
          </Alert>

          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>{t("progresoAlumno.tabla.asignatura")}</b>
                  </TableCell>
                  <TableCell>
                    <b>{t("progresoAlumno.tabla.proyecto")}</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>{t("progresoAlumno.tabla.horas")}</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>{t("progresoAlumno.tabla.ultimaSesion")}</b>
                  </TableCell>
                  <TableCell width={240}>
                    <b>{t("progresoAlumno.tabla.progreso")}</b>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {status.result.rows.map((r) => {
                  /* aseguramos que nunca sean undefined */
                  const hechas = r.horasHechas ?? 0;
                  const total = r.horasTotal ?? 0;
                  const pct = total ? (hechas / total) * 100 : 0;

                  return (
                    <TableRow key={`${r.asignatura}-${r.proyecto}`}>
                      <TableCell>{r.asignatura}</TableCell>
                      <TableCell>{r.proyecto}</TableCell>
                      <TableCell align="right">
                        {hechas.toFixed(1)} / {total.toFixed(1)}
                      </TableCell>
                      <TableCell align="center">{r.ultimaSesion}</TableCell>
                      <TableCell>
                        <LinearProgress variant="determinate" value={pct} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Stack>
  );
}
